import React, { useEffect, useRef, useState } from 'react';
import { useContext } from 'react';
import { store } from 'components/Store';
import { useContentResults } from 'components/Dashboard/ContentResultsContext';
import CrmContactItem from './CrmContactItem/CrmContactItem';
import CrmContactsTableSkeleton, { CrmContactItemSkeleton } from './CrmContactsSkeleton';
import { useCallback } from 'react';
import { preventHistoryBack } from 'components/Dashboard/helpers/preventHistoryBack';
import useGetSectionFilters from 'components/Dashboard/Filters/useGetSectionFilters';
import EmptyMessage from 'components/Dashboard/components/EmptyMessage';
import isDummyTableItem from 'components/Dashboard/helpers/isDummyTableItem';
import { loadMoreTableRowsDependOnScreen } from 'components/Dashboard/helpers/loadMoreTableRowsDependOnScreen';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import TableComponent from 'components/Dashboard/Influence/tableComponents/TableComponent';
import useSelectCrmContacts from './useSelectCrmContacts';
import useGeneralApiCall from 'components/Dashboard/apiCalls/useGeneralApiCall';
import useTopicOrClientWord from 'components/Dashboard/hooks/useTopicOrClientWord';
import NoContactsGroupEmptyMessage from './NoContactsGroupEmptyMessage';
import WaitingAnimationScreen from 'components/Dashboard/StakeHolders/WaitingAnimationScreen';
import { useFilters } from 'components/Dashboard/Filters/FiltersContext';
import useAddMetaTags from 'components/Dashboard/utilities/addMetaTags';
import { RoleOptions, RoleSearchOptions } from 'components/Dashboard/Filters/FiltersData/StakeholdersFilters';
import getUrlParam, { decodeUrlQueryValue } from 'components/Dashboard/utilities/getUrlParam';

const CrmContactsTable = ({
  selectedCrmContacts,
  setSelectedCrmContacts,
  setTotalHits,
  isLoadingState,
  setShowAddTagsModal,
}) => {
  const { isLoading, setIsLoading } = isLoadingState ?? {};
  const params = useParams();
  const location = useLocation();
  const { groupId } = params;

  const columns = [
    {
      accessorKey: 'checkbox',
      header: '',
      ref: useRef(null),
      type: 'checkbox',
    },
    {
      accessorKey: 'lastName',
      header: 'Name',
      ref: useRef(null),
    },
    {
      accessorKey: 'championStatus',
      header: 'Champion status',
      ref: useRef(null),
    },
    {
      accessorKey: 'role',
      header: 'Role',
      ref: useRef(null),
    },
    {
      accessorKey: 'description',
      header: 'Description',
      ref: useRef(null),
    },
    {
      accessorKey: 'email',
      header: 'Email',
      ref: useRef(null),
    },
    {
      accessorKey: 'tags',
      header: 'Tags',
      ref: useRef(null),
    },
    /* {
      accessorKey: 'createdAtUtc',
      header: `Date added`,
      ref: useRef(null),
    }, */
    {
      accessorKey: 'salutation',
      header: 'Salutation',
      ref: useRef(null),
    },
  ];

  const globalState = useContext(store);
  const { state } = globalState;
  const { contentResults, setContentResults } = useContentResults();
  const { addedCustomContacts, crmContactsTableOnSort, removedContactsQntty, group, contactOnGroupUpdated, team } =
    state;

  const includeStaff = getUrlParam('includeStaff') === 'true';

  const { getSectionFilters } = useGetSectionFilters('InfluenceContacts');
  const { contentFilters } = useFilters();

  const sectionFilters = getSectionFilters();
  const { hash, filters } = sectionFilters;

  const { CrmContacts } = contentResults;
  const [currentlyProcessing, setCurrentlyProcessing] = useState({
    attempt: 0,
    state: false,
  });
  const [scrollTopContainer, setScrollTopContainer] = useState({
    scrollTop: 0,
    scrollHeight: 0,
    clientHeight: 0,
  });

  const query = new URLSearchParams(location.search);
  const sort = query.get('sort') ? query.get('sort').split('_') : null;
  const searchQuery = query.get('search') ? decodeUrlQueryValue(query.get('search')) : '';

  const [localQuery, setLocalQuery] = useState(searchQuery);
  const [activeIndex, setActiveIndex] = useState(null);
  const [pageSize, setPageSize] = useState(11);
  const [scrollPositionX, setScrollPositionX] = useState(crmContactsTableOnSort?.scrollPositionX);
  const [pageIsLoading, setPageIsLoading] = useState(null);
  const rowRef = useRef(null);

  const navigate = useNavigate();
  const scrollCondition = CrmContacts && CrmContacts.scrollPosition;

  const { selectedIds, selectedAll, selectedAllVisible } = selectedCrmContacts;
  const { generalApiCall } = useGeneralApiCall();
  const { newAgencyPlan } = useTopicOrClientWord();
  const { addMetaTags } = useAddMetaTags();

  const initialCall = useRef(null);
  initialCall.current = (source) => {
    setCurrentlyProcessing({ attempt: 0, state: false });
    const loadResults = async () => {
      try {
        await createResults({ source });
      } catch (error) {
        console.error(error);
      }
    };
    if (!scrollCondition) {
      loadResults();
    } else {
      setIsLoading(false);
      //AE: Necessary to have a setTimeOut to update the scrollPosition and allow continue using the list correctly. (Without this if you clear the search or change the topic it will not work.)
      setTimeout(() => {
        let CrmContactsWithoutScroll = {
          ...CrmContacts,
          scrollPosition: null,
        };
        setContentResults({
          ...contentResults,
          CrmContacts: CrmContactsWithoutScroll,
        });
      }, 300);
    }
  };
  useEffect(() => {
    const source = new AbortController();
    setScrollTopContainer({
      scrollTop: 0,
      scrollHeight: 0,
      clientHeight: 0,
    });
    initialCall.current(source);

    return () => {
      source.abort('CrmContacts canceled by the user.');
    };
  }, [
    hash,
    location.search,
    contentFilters,
    addedCustomContacts.length,
    removedContactsQntty,
    crmContactsTableOnSort,
    group,
    contactOnGroupUpdated,
    team,
    includeStaff,
  ]);

  useEffect(() => {
    if (!isLoading && scrollPositionX) {
      scrollBarRef.current.scrollLeft = scrollPositionX;
    }
  }, [isLoading, CrmContacts]);

  async function createResults({ pageNumber, processing, source: sourceProp }) {
    if (pageNumber) {
      setPageIsLoading(pageNumber);
    }
    try {
      if (!pageNumber && !processing) {
        setIsLoading(true);
      }
      const pathname = `/api/crm-contact/${groupId ? 'preview-saved-crm-group' : 'query'}`;
      let requestProperties = {
        pageSize: pageSize,
        pageNumber: pageNumber ?? 1,
      };

      const staffersFilters = [...RoleOptions, ...RoleSearchOptions]?.filter((item) =>
        item?.name?.toLowerCase()?.includes('staff')
      );
      const filtersValues = staffersFilters?.map((item) => item?.filterValue ?? item?.name);
      const includeStaffers = includeStaff || filters?.some((item) => filtersValues?.includes(item?.value));

      const noStaffFilter = staffersFilters?.map((item) => {
        return { field: 'categories', value: item?.filterValue ?? item?.name, operator: 'str_not_eq' };
      });

      const filtersToUse = includeStaffers ? filters : [...noStaffFilter, ...filters];

      if (groupId) {
        requestProperties = {
          ...requestProperties,
          id: parseInt(groupId),
          query: searchQuery,
        };
      } else {
        requestProperties = {
          ...requestProperties,
          filters: filtersToUse,
          query: searchQuery,
        };
      }
      if (sort && (searchQuery === localQuery || searchQuery === '')) {
        requestProperties = {
          ...requestProperties,
          sorts: [
            { field: sort[0], order: sort[1] },
            { field: 'id', order: 'desc' },
          ],
        };
      }

      if (searchQuery !== localQuery) {
        setLocalQuery(searchQuery);
      }

      let apiCallProperties = {
        pathname,
        method: 'post',
        requestProperties,
        needsAuthentication: true,
        requestSource: sourceProp,
        notShowErrorMessage: true,
      };

      let results = await generalApiCall(apiCallProperties);
      let totalResultsNumber;
      //AE: It will create a duplicated call but it's necessary to show the total number of results in the badge.
      if (!!groupId && !!searchQuery) {
        const totalResults = await generalApiCall({
          ...apiCallProperties,
          requestProperties: { ...requestProperties, query: '' },
        });
        if (!!totalResults) {
          totalResultsNumber = totalResults?.totalHits;
        }
      } else {
        totalResultsNumber = results?.totalHits;
      }

      if (!!results) {
        let contactsIds = pageNumber ? CrmContacts.contactsIds : [];
        let hitsResults = pageNumber ? CrmContacts.hits : [];

        if (results.hits !== null) {
          contactsIds = contactsIds.concat(results.contactsIds);
          hitsResults = hitsResults.concat(results.hits);
        }
        let currentId = results.hits !== null && results.hits.length > 0 ? results.hits[0].id : 0;
        let ResultsToState = {
          ...results,
          currentId: currentId,
          contactsIds: contactsIds,
          currentPositionId: contactsIds.indexOf(currentId),
          hits: hitsResults,
          thereIsMoreContent: results.hits !== null ? results.hits.length >= pageSize : false,
          readyCrmContactsFilters: filters?.length > 0 || !!searchQuery,
          searchQuery,
          totalResultsNumber,
        };

        setContentResults({
          ...contentResults,
          CrmContacts: ResultsToState,
        });

        setTotalHits(ResultsToState?.totalHits);
        setCurrentlyProcessing({ attempt: currentlyProcessing.attempt + 1, state: results?.crmRefreshInProgress });
        setIsLoading(false);
        setPageIsLoading(null);
      }
    } catch (error) {}
  }

  //SET FILTERS TO URL
  useEffect(() => {
    if (crmContactsTableOnSort?.sort?.length) {
      query.set('sort', crmContactsTableOnSort?.sort);
    }
    let urlToHistory = `${location.pathname}?${query.toString()}${hash !== '' ? `#${hash}` : ''}`;
    if (!groupId) {
      let title = `Directory - Influence - PolicyMogul`;
      addMetaTags({ title, hash });
    }
    setTimeout(() => {
      navigate(urlToHistory, { replace: true, state: location.state });
    }, 0);
  }, [hash, crmContactsTableOnSort]);

  useEffect(() => {
    const source = new AbortController();

    const callAgainResults = () => {
      setTimeout(async () => {
        try {
          await createResults({ processing: true, source });
        } catch (error) {
          console.error(error);
        }
      }, 5000);
    };
    const { state } = currentlyProcessing;
    if (state === true) {
      callAgainResults();
    }
    return () => {
      if (currentlyProcessing.state && !!source) {
        source.abort('New call canceled by the user.');
      }
    };
  }, [currentlyProcessing]);

  //LOAD ONSCROLL
  const load_more_results = async () => {
    if (CrmContacts !== undefined) {
      let { thereIsMoreContent, pageNumber } = CrmContacts;
      if (thereIsMoreContent) {
        await createResults({ pageNumber: pageNumber + 1 });
      }
    }
  };

  const scrollBarRef = useRef(null);
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    setScrollTopContainer({ scrollTop, scrollHeight, clientHeight });
    const scrolledToBottom = Math.ceil(scrollTop + clientHeight) >= scrollHeight;
    if (scrolledToBottom && pageIsLoading === null) {
      setScrollPositionX(0);
      load_more_results();
    }
  };

  const isDummyItem = useCallback((props) => isDummyTableItem(props), [scrollTopContainer]);

  let hitsToRender = CrmContacts?.hits ?? [];

  const { handleCheck } = useSelectCrmContacts();

  const contactIds = CrmContacts?.hits.map((item) => item.id);
  const isSelected = selectedIds.length > 0 || selectedAll || selectedAllVisible.length > 0;
  //PREVENT HISTORY BACK ON TRACKPAD SCROLL
  useEffect(() => {
    if (scrollBarRef.current) {
      scrollBarRef.current.addEventListener('mousewheel', (e) => preventHistoryBack(e, scrollBarRef.current));
      loadMoreTableRowsDependOnScreen({
        containerHeight: scrollBarRef.current.clientHeight,
        cellHeight: 62,
        headerHeight: 50,
        setPageSize,
        pageSize,
      });
    }
  }, [scrollBarRef.current]);

  useEffect(() => {
    if (pageSize > 11) {
      load_more_results();
    }
  }, [pageSize]);

  return (
    <div className={`row mx-0 h-100 ${!isLoading && !CrmContacts?.totalHits ? 'pr-md-4 pr-3' : ''}`}>
      <div className='h-100 w-100' style={isLoading ? { overflow: 'hidden' } : null}>
        {isLoading ? (
          <CrmContactsTableSkeleton />
        ) : (
          <>
            {!CrmContacts?.totalHits && !currentlyProcessing?.state ? (
              <>
                <div className='pt-2'>
                  <div className='contact-list-scrollbar-table-no-data h-100'>
                    {!query.get('search') && !filters.length ? (
                      <>
                        {groupId ? (
                          <NoContactsGroupEmptyMessage />
                        ) : (
                          <div
                            className='crm-table-no-contacts w-100'
                            style={{
                              paddingLeft: 0,
                              paddingRight: 0,
                              paddingTop: '52px',
                              gap: '5px',
                              backgroundColor: 'rgba(0, 148, 204, 0.04)',
                            }}
                          >
                            <p className='crm-table-no-contacts-title pt-2'>
                              Your contact list {newAgencyPlan ? ` for ${team?.teamName}` : ''} is empty
                            </p>
                            <p className='crm-table-no-contacts-description'>
                              Once you or your teammates add contacts, you’ll be able to add them to email campaigns and
                              take other actions
                            </p>
                            <div className='no-contacts-actions-container w-100'>
                              <div className='w-100 h-100'>
                                <img
                                  className='w-100 rounded-top pt-4'
                                  src={`${import.meta.env.VITE_APP_CDNURL}/images/no-contacts-img.jpg`}
                                  alt='No contacts'
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className='w-100'>
                        <EmptyMessage contactsTable emptyMessageResourceName={'contacts'} />
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : (
              <>
                {currentlyProcessing.state ? (
                  <WaitingAnimationScreen waitingMessage={'your contacts'} />
                ) : (
                  <div className='contact-list-scrollbar-table-wrapper'>
                    <div className='stakeholder-list-content-scrollbar' />
                    <div className='contact-list-scrollbar-table' onScroll={handleScroll} ref={scrollBarRef}>
                      {hitsToRender.length > 0 && (
                        <TableComponent
                          setScrollPositionX={setScrollPositionX}
                          scrollBarRef={scrollBarRef}
                          columns={columns}
                          additionalClass={'crm-table-contacts'}
                          selectedState={{ selectedAll, isSelected, handleCheck }}
                          ascAbrev={'asc'}
                          descAbrev={'desc'}
                          activeIndexState={{ activeIndex, setActiveIndex }}
                          contactsTable={true}
                          stateProperty={'crmContactsTableOnSort'}
                        >
                          {hitsToRender.map((item, index, array) => {
                            return (
                              <CrmContactItem
                                ref={index === 1 ? rowRef : undefined}
                                array={array}
                                item={item}
                                key={`item${item.contactId}-${index}`}
                                index={index}
                                setSelectedCrmContacts={setSelectedCrmContacts}
                                selectedCrmContacts={selectedCrmContacts}
                                contactIds={contactIds}
                                hitsToRender={hitsToRender}
                                dummyItem={
                                  CrmContacts.totalHits > 50
                                    ? isDummyItem({
                                        index,
                                        itemHeight: 67,
                                        cachedInvisibleItems: 1,
                                        scrollTopContainer,
                                        pageSize,
                                      })
                                    : false
                                }
                                setShowAddTagsModal={setShowAddTagsModal}
                                scrollBarRef={scrollBarRef}
                                activeIndex={activeIndex}
                              />
                            );
                          })}
                          {CrmContacts?.thereIsMoreContent && <CrmContactItemSkeleton />}
                        </TableComponent>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CrmContactsTable;
